/**
 * React Query hooks for the IBS admission surface (P97).
 *
 * GET  /v1/admin/twin/ibses                                  candidates
 * POST /v1/admin/twin/ibses/{id}/derive-admission            Engine derive
 * POST /v1/admin/twin/ibses/{id}/admission-decisions/{d}/signoff
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  IBSAdmissionDecision,
  IBSCandidate,
  IBSReviewScan,
  PromotableServices,
} from "shared/api/types";

export const ibsAdmissionKeys = {
  candidates: () => ["ibs-admission", "candidates"] as const,
  promotable: () => ["ibs-admission", "promotable-services"] as const,
  reviewScan: () => ["ibs-admission", "review-scan"] as const,
};

export function useIBSCandidates(): UseQueryResult<IBSCandidate[], ApiError> {
  return useQuery({
    queryKey: ibsAdmissionKeys.candidates(),
    queryFn: () => api.get<IBSCandidate[]>("/v1/admin/twin/ibses"),
  });
}

export function useIBSReviewScan(
  /** Round 18 G28 -- the scan is the resilience desk's (and an
   *  administrator's); a CRO opening the admission page was refused
   *  403, in the console only. */
  enabled = true,
): UseQueryResult<IBSReviewScan, ApiError> {
  return useQuery({
    queryKey: ibsAdmissionKeys.reviewScan(),
    queryFn: () => api.get<IBSReviewScan>("/v1/admin/twin/ibses/review-scan"),
    enabled,
  });
}

/**
 * The promotable services, and enough to say why there are none.
 *
 * A1 -- a flat list cannot tell an empty register from one whose every
 * service is already promoted, and the panel needs a different sentence
 * for each. The counts come from the endpoint rather than being
 * inferred here, because inferring them would mean a second source of
 * truth for the eligibility rule.
 */
export function usePromotableServices(): UseQueryResult<PromotableServices, ApiError> {
  return useQuery({
    queryKey: ibsAdmissionKeys.promotable(),
    queryFn: () =>
      api.get<PromotableServices>("/v1/admin/twin/ibses/promotable-services"),
  });
}

export interface PromoteArgs {
  serviceId: string;
  outcomeCategory: string;
  rationale: string;
  sovereignFlag: boolean;
  provenanceStream: string;
}

export function usePromoteService() {
  const qc = useQueryClient();
  return useMutation<IBSCandidate, ApiError, PromoteArgs>({
    mutationFn: ({
      serviceId,
      outcomeCategory,
      rationale,
      sovereignFlag,
      provenanceStream,
    }) =>
      api.post<IBSCandidate>("/v1/admin/twin/ibses/promote", {
        service_id: serviceId,
        outcome_category: outcomeCategory,
        rationale,
        sovereign_flag: sovereignFlag,
        provenance_stream: provenanceStream,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ibsAdmissionKeys.candidates() });
      void qc.invalidateQueries({ queryKey: ibsAdmissionKeys.promotable() });
    },
  });
}

export function useDeriveAdmission() {
  const qc = useQueryClient();
  return useMutation<IBSAdmissionDecision, ApiError, { ibsId: string }>({
    mutationFn: ({ ibsId }) =>
      api.post<IBSAdmissionDecision>(`/v1/admin/twin/ibses/${ibsId}/derive-admission`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ibsAdmissionKeys.candidates() }),
  });
}

/** Round 20 W10 -- the scale and every criterion's bands, from the one
 *  table the rules read. */
export interface AdmissionCriterion {
  key: string;
  label: string;
  owner: string;
  question: string;
  bands: string[];
}
/** P416 -- one pathway of the active regime's rubric. */
export interface AdmissionPathwayCriteria {
  code: string;
  label: string;
  gates: { key: string; question: string }[];
  criteria: AdmissionCriterion[];
}
export interface AdmissionCriteria {
  /** P416 -- whose rubric the form is reading, and its edition. */
  regime_code?: string;
  rubric_version?: string;
  min_score: number;
  max_score: number;
  pathways?: AdmissionPathwayCriteria[];
  /** The SAMA streams by name, kept for pre-P416 readers. */
  ces: AdmissionCriterion[];
  ids: AdmissionCriterion[];
}
export function useAdmissionCriteria() {
  return useQuery({
    queryKey: ["ibs-admission-criteria"],
    queryFn: () => api.get<AdmissionCriteria>("/v1/admin/twin/ibses/criteria"),
    staleTime: Infinity,
  });
}

// A4 -- the same rules, a sheet proposed by a person. The decision,
// pathway and tier are still derived server-side; only the proposal
// changes hands, and the row records `source: "manual"`.
export function useScoreAdmissionManually() {
  const qc = useQueryClient();
  return useMutation<
    IBSAdmissionDecision,
    ApiError,
    { ibsId: string; scores: Record<string, number | boolean>; rationale?: string }
  >({
    mutationFn: ({ ibsId, scores, rationale }) =>
      api.post<IBSAdmissionDecision>(
        `/v1/admin/twin/ibses/${ibsId}/admission-decisions/manual`,
        { scores, ...(rationale ? { rationale } : {}) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ibsAdmissionKeys.candidates() }),
  });
}

// P221: reverse-admission -- mint an excluded decision for an admitted IBS.
export function useRequestRemoval() {
  const qc = useQueryClient();
  return useMutation<IBSCandidate, ApiError, { ibsId: string; rationale: string }>({
    mutationFn: ({ ibsId, rationale }) =>
      api.post<IBSCandidate>(`/v1/admin/twin/ibses/${ibsId}/request-removal`, {
        rationale,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ibsAdmissionKeys.candidates() }),
  });
}

export interface ChallengeArgs {
  ibsId: string;
  decisionId: string;
  /** A5 -- the reviewer is the session; only the note is typed. */
  note?: string;
  /** Round 20 G45 -- required when Q2's Engine and evidence disagree. */
  reconciliation?: string;
}

export function useChallengeAdmission() {
  const qc = useQueryClient();
  return useMutation<IBSAdmissionDecision, ApiError, ChallengeArgs>({
    mutationFn: ({ ibsId, decisionId, note, reconciliation }) =>
      api.post<IBSAdmissionDecision>(
        `/v1/admin/twin/ibses/${ibsId}/admission-decisions/${decisionId}/challenge`,
        reconciliation ? { note, reconciliation } : { note },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ibsAdmissionKeys.candidates() }),
  });
}

export interface SignoffArgs {
  ibsId: string;
  decisionId: string;
  /** A5 -- the seat only. The signer is the authenticated session, so
   *  the same actor is recorded whichever route the signature takes. */
  role: "cro" | "business_owner" | "cio";
}

export function useSignoffAdmission() {
  const qc = useQueryClient();
  return useMutation<IBSAdmissionDecision, ApiError, SignoffArgs>({
    mutationFn: ({ ibsId, decisionId, role }) =>
      api.post<IBSAdmissionDecision>(
        `/v1/admin/twin/ibses/${ibsId}/admission-decisions/${decisionId}/signoff`,
        { role },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ibsAdmissionKeys.candidates() }),
  });
}
